import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { CircuitEvent, Dispatch } from '../lib/types';

marked.setOptions({ gfm: true, breaks: false });

type Props = { circuit: CircuitEvent[]; dispatch: Dispatch };

export function QuarterlyDispatch({ circuit, dispatch }: Props) {
  // Body: prefer prebaked body_html if non-empty, else marked(body_md).
  const bodyHtml = useMemo(() => {
    if (dispatch.body_html && dispatch.body_html.trim()) {
      return DOMPurify.sanitize(dispatch.body_html, {
        USE_PROFILES: { html: true },
      });
    }
    const rendered = marked.parse(dispatch.body_md || '', { async: false }) as string;
    return DOMPurify.sanitize(rendered, { USE_PROFILES: { html: true } });
  }, [dispatch.body_md, dispatch.body_html]);

  const signoffHtml = useMemo(
    () =>
      DOMPurify.sanitize(dispatch.signoff_html || '', {
        USE_PROFILES: { html: true },
      }),
    [dispatch.signoff_html]
  );

  return (
    <section className="section" style={{ padding: '72px 28px 80px' }}>
      <div
        className="grid-dispatch"
        style={{
          display: 'grid',
          gridTemplateColumns: '200px 620px 280px',
          columnGap: 56,
          alignItems: 'start',
          maxWidth: 1212,
          margin: '0 auto',
        }}
      >
        {/* left meta */}
        <div className="dispatch-meta" style={{ position: 'sticky', top: 40 }}>
          <div className="eyebrow-accent" style={{ marginBottom: 8 }}>
            Quarterly Dispatch
          </div>
          <div
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 1.2,
              opacity: 0.55,
            }}
          >
            {dispatch.issue_label}
          </div>
          <div
            style={{
              height: 1,
              background: 'var(--ink)',
              opacity: 0.25,
              margin: '28px 0',
            }}
          />
          <div className="serif italic" style={{ fontSize: 26, lineHeight: 1.15 }}>
            From the field.
          </div>
          <div
            className="serif italic"
            style={{ fontSize: 15, marginTop: 6, opacity: 0.7 }}
          >
            A letter, of sorts.
          </div>
          <div
            style={{
              marginTop: 36,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 1.6,
              opacity: 0.5,
            }}
          >
            Filed · {dispatch.filed_location}
          </div>
          <div
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 1.6,
              opacity: 0.5,
              marginTop: 4,
            }}
          >
            ~ {dispatch.read_minutes} min read
          </div>
          <Link
            to="/dispatches"
            style={{
              display: 'inline-block',
              marginTop: 18,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 1.6,
              color: 'var(--accent)',
              textDecoration: 'none',
              borderBottom: '1px solid var(--accent)',
              paddingBottom: 2,
            }}
          >
            Past issues →
          </Link>
        </div>

        {/* body column — DB-driven prose + sign-off */}
        <div>
          <div
            className="serif dispatch-body"
            style={{ fontSize: 19, lineHeight: 1.7 }}
            dangerouslySetInnerHTML={{ __html: bodyHtml }}
          />
          {signoffHtml && (
            <div
              className="serif italic"
              style={{
                fontSize: 17,
                marginTop: 28,
                opacity: 0.8,
                lineHeight: 1.5,
              }}
              dangerouslySetInnerHTML={{ __html: signoffHtml }}
            />
          )}
        </div>

        {/* right rail: on the circuit */}
        <aside className="dispatch-circuit" style={{ position: 'sticky', top: 40 }}>
          <div className="eyebrow-accent" style={{ marginBottom: 4 }}>
            On the circuit
          </div>
          <div
            className="serif italic"
            style={{ fontSize: 13, opacity: 0.65, marginBottom: 18 }}
          >
            Where to find me, live.
          </div>
          {circuit.map((e, i, arr) => (
            <div
              key={`${e.when_label}-${e.where}`}
              style={{
                padding: '12px 0',
                borderBottom:
                  i === arr.length - 1 ? 'none' : '1px solid var(--ink-22)',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: 1.6,
                  opacity: 0.55,
                  marginBottom: 5,
                }}
              >
                {e.when_label}
              </div>
              <div className="serif" style={{ fontSize: 17, lineHeight: 1.2 }}>
                {e.where}
              </div>
              <div
                className="serif italic"
                style={{
                  fontSize: 12.5,
                  opacity: 0.65,
                  marginTop: 3,
                  lineHeight: 1.35,
                }}
              >
                {e.talk} · {e.city}
              </div>
            </div>
          ))}
        </aside>
      </div>
    </section>
  );
}
